import moment from 'moment'
import * as React from 'react'
import { useState } from 'react'

import EditStudentAccountModal from './edit_student_account_modal'
import MergeStudentAccountsModal from './merge_student_accounts_modal'
import MoveStudentsModal from './move_students_modal'
import { effectiveReadAloud, onOrOff } from './read_aloud_setting'
import ReadAloudSettingsModal from './read_aloud_settings_modal'
import RemoveStudentsModal from './remove_students_modal'
import ResetStudentPasswordModal from './reset_student_password_modal'
import { canvasProvider, classroomProviders, cleverProvider, googleProvider, classlinkProvider, nycpsClassroomProvider, nycpsProvider, providerConfigLookup, teacherImportsStudents } from './providerHelpers'
import { activeHeaders, archivedHeaders } from './classroom_student_section_headers'
import { DuplicateAccountProTip, ProviderNoteOfExplanation } from './classroom_student_section_notes'

import { DataTable, DropdownInput, Tooltip, helpIcon, warningIcon, } from '../../../Shared/index'

const canvasSetupInstructionsPdf = `${window.postbuildEnv.CDN_URL}/documents/setup_instructions_pdfs/canvas_setup_instructions.pdf`
const cleverSetupInstructionsPdf = `${window.postbuildEnv.CDN_URL}/documents/setup_instructions_pdfs/clever_setup_instructions.pdf`
const googleSetupInstructionsPdf = `${window.postbuildEnv.CDN_URL}/documents/setup_instructions_pdfs/google_setup_instructions.pdf`
const classlinkSetupInstructionsPdf = `${window.postbuildEnv.CDN_URL}/documents/setup_instructions_pdfs/classlink_setup_instructions.pdf`

enum modalNames {
  editStudentAccountModal = 'editStudentAccountModal',
  resetStudentPasswordModal = 'resetStudentPasswordModal',
  mergeStudentAccountsModal = 'mergeStudentAccountsModal',
  moveStudentsModal = 'moveStudentsModal',
  removeStudentsModal = 'removeStudentsModal',
  readAloudSettingsModal = 'readAloudSettingsModal',
}

interface ClassroomStudentSectionProps {
  classroom: any;
  classrooms: Array<any>;
  isOwnedByCurrentUser: boolean;
  provider?: string;
  user: any;
  onSuccess: (event) => void;
  inviteStudents?: (event) => void;
  importProviderClassroomStudents?: (event) => void;
  viewAsStudent?: (event) => void;
}

const ClassroomStudentSection = ({
  classroom,
  classrooms,
  importProviderClassroomStudents,
  inviteStudents,
  isOwnedByCurrentUser,
  onSuccess,
  user,
  viewAsStudent,
}: ClassroomStudentSectionProps) => {
  const [selectedStudentIds, setSelectedStudentIds] = useState<Array<string | number>>([])
  const [studentIdsForModal, setStudentIdsForModal] = useState<Array<string | number>>([])
  const [showModal, setShowModal] = useState<modalNames>()

  // The provider every student shares, or null; the flags below are mutually exclusive so one pass settles them all.
  const firstStudentProvider = classroom.students[0]?.provider
  const soleStudentProvider = firstStudentProvider && classroom.students.every(student => student.provider === firstStudentProvider) ? firstStudentProvider : null
  const allStudentsAreProvider = (provider: string) => soleStudentProvider === provider

  const allStudentsAreCanvas = allStudentsAreProvider(canvasProvider)
  const allStudentsAreClever = allStudentsAreProvider(cleverProvider)
  const allStudentsAreGoogle = allStudentsAreProvider(googleProvider)
  const allStudentsAreClasslink = allStudentsAreProvider(classlinkProvider)
  const allStudentsAreNycps = allStudentsAreProvider(nycpsProvider)
  const readAloudAvailable = !!classroom.read_aloud?.available

  const individualStudentActions = () => {
    return {
      editAccount: {
        name: 'Edit account',
        action: (id) => editStudentAccount(id)
      },
      resetPassword: {
        name: 'Reset password',
        action: (id) => resetStudentPassword(id)
      },
      mergeAccounts: {
        name: 'Merge accounts',
        action: (id) => mergeStudentAccounts(id)
      },
      moveClass: {
        name: 'Move class',
        action: (id) => moveClass(id)
      },
      removeFromClass: {
        name: 'Remove from class',
        action: (id) => removeStudentFromClass(id)
      },
      viewAsStudent: {
        name: 'View as student',
        action: (id) => viewAsStudent(id)
      },
      readAloudSettings: {
        name: 'Read aloud settings',
        action: (id) => readAloudSettings(id)
      }
    }
  }

  const dropdownActions = () => {
    return {
      editAccount: {
        label: 'Edit account',
        action: editStudentAccount,
        value: 'editAccount'
      },
      resetPassword: {
        label: 'Reset password',
        action: resetStudentPassword,
        value: 'resetPassword'
      },
      mergeAccounts: {
        label: 'Merge accounts',
        action: mergeStudentAccounts,
        value: 'mergeAccounts'
      },
      moveClass: {
        label: 'Move class',
        action: moveClass,
        value: 'moveClass'
      },
      removeFromClass: {
        label: 'Remove from class',
        action: removeStudentFromClass,
        value: 'removeFromClass'
      },
      viewAsStudent: {
        label: 'View as student',
        action: viewAsStudent,
        value: 'viewAsStudent'
      },
      readAloudSettings: {
        label: 'Read aloud settings',
        action: readAloudSettings,
        value: 'readAloudSettings'
      }
    }
  }

  const isMergedOriginStudent = (studentId) => {
    return !!classroom.merged_origin_student_ids?.includes(studentId)
  }

  const actionsForIndividualStudent = (student) => {
    const { user_external_id, synced } = student

    const {
      editAccount,
      resetPassword,
      viewAsStudent,
      mergeAccounts,
      moveClass,
      removeFromClass,
      readAloudSettings
    } = individualStudentActions()

    let actions
    if (user_external_id) {
      actions = synced ? [viewAsStudent] : [viewAsStudent, moveClass, removeFromClass]
    } else if (classrooms.length > 1 && isOwnedByCurrentUser) {
      actions = [editAccount, resetPassword, viewAsStudent, mergeAccounts, moveClass, removeFromClass]
    } else if (isOwnedByCurrentUser) {
      actions = [editAccount, resetPassword, viewAsStudent, mergeAccounts, removeFromClass]
    } else {
      actions = [editAccount, resetPassword, viewAsStudent, removeFromClass]
    }

    if (isMergedOriginStudent(student.id)) {
      actions = actions.filter(action => action !== mergeAccounts && action !== moveClass)
    }

    return readAloudAvailable ? [...actions, readAloudSettings] : actions
  }

  const handleSuccess = (successMessage) => {
    setSelectedStudentIds([])
    onSuccess(successMessage)
  }

  const checkRow = (id) => {
    setSelectedStudentIds(selectedStudentIds.concat(id))
  }

  const uncheckRow = (id) => {
    setSelectedStudentIds(selectedStudentIds.filter(selectedId => selectedId !== id))
  }

  const checkAllRows = () => {
    setSelectedStudentIds(classroom.students.map(student => student.id))
  }

  const uncheckAllRows = () => {
    setSelectedStudentIds([])
  }

  const handleClickViewAsStudentButton = () => {
    viewAsStudent(null)
  }


  const selectAction = (action) => {
    action.action()
  }

  const editStudentAccount = (id = null) => {
    // we will only show the edit student account dropdown option when only one student is selected
    setShowModal(modalNames.editStudentAccountModal)
    setStudentIdsForModal([id || selectedStudentIds[0]])
  }

  const resetStudentPassword = (id = null) => {
    // we will only show the reset password account dropdown option when only one student is selected
    setShowModal(modalNames.resetStudentPasswordModal)
    setStudentIdsForModal([id || selectedStudentIds[0]])
  }

  const mergeStudentAccounts = (id = null) => {
    // we will only show the merge student accounts account dropdown option when one or two students are selected
    setShowModal(modalNames.mergeStudentAccountsModal)
    setStudentIdsForModal(id ? [id] : selectedStudentIds)
  }

  const moveClass = (id = null) => {
    // we will show the move class dropdown option when any number of students are selected
    setShowModal(modalNames.moveStudentsModal)
    setStudentIdsForModal(id ? [id] : selectedStudentIds)
  }

  const removeStudentFromClass = (id = null) => {
    // we will show the remove student from class dropdown option when any number of students are selected
    const studentIds = id ? [id] : selectedStudentIds
    setShowModal(modalNames.removeStudentsModal)
    setStudentIdsForModal(id ? [id] : selectedStudentIds)
  }

  const readAloudSettings = (id = null) => {
    setShowModal(modalNames.readAloudSettingsModal)
    setStudentIdsForModal(id ? [id] : selectedStudentIds)
  }

  const closeModal = () => {
    setShowModal(null)
    setStudentIdsForModal([])
  }

  const renderEditStudentAccountModal = () => {
    if (showModal === modalNames.editStudentAccountModal && studentIdsForModal.length === 1) {
      const student = classroom.students.find(s => s.id === studentIdsForModal[0])
      return (
        <EditStudentAccountModal
          classroom={classroom}
          close={closeModal}
          onSuccess={handleSuccess}
          student={student}
        />
      )
    }
  }

  const renderResetStudentPasswordModal = () => {
    if (showModal === modalNames.resetStudentPasswordModal && studentIdsForModal.length === 1) {
      const student = classroom.students.find(s => s.id === studentIdsForModal[0])
      return (
        <ResetStudentPasswordModal
          classroom={classroom}
          close={closeModal}
          onSuccess={handleSuccess}
          student={student}
        />
      )
    }
  }

  const renderMergeStudentAccountsModal = () => {
    if (showModal === modalNames.mergeStudentAccountsModal) {
      return (
        <MergeStudentAccountsModal
          classroom={classroom}
          close={closeModal}
          onSuccess={handleSuccess}
          selectedStudentIds={studentIdsForModal}
        />
      )
    }
  }

  const renderMoveStudentsModal = () => {
    if (showModal === modalNames.moveStudentsModal) {
      return (
        <MoveStudentsModal
          classroom={classroom}
          classrooms={classrooms}
          close={closeModal}
          onSuccess={handleSuccess}
          selectedStudentIds={studentIdsForModal}
        />
      )
    }
  }

  const renderRemoveStudentsModal = () => {
    if (showModal === modalNames.removeStudentsModal) {
      return (
        <RemoveStudentsModal
          classroom={classroom}
          close={closeModal}
          onSuccess={handleSuccess}
          selectedStudentIds={studentIdsForModal}
        />
      )
    }
  }

  const renderReadAloudSettingsModal = () => {
    if (showModal === modalNames.readAloudSettingsModal) {
      return (
        <ReadAloudSettingsModal
          classroom={classroom}
          close={closeModal}
          isOwnedByCurrentUser={isOwnedByCurrentUser}
          onSuccess={handleSuccess}
          students={classroom.students.filter(student => studentIdsForModal.includes(student.id))}
        />
      )
    }
  }

  const optionsForStudentActions = () => {
    const anySelectedProviderStudents = selectedStudentIds.some(id => {
      const student = classroom.students.find(s => s.id === id)
      if (!student) { return false }
      return student.external_user_id
    })
    const anySelectedMergedOriginStudents = selectedStudentIds.some(isMergedOriginStudent)

    const {
      editAccount,
      resetPassword,
      mergeAccounts,
      moveClass,
      removeFromClass,
      viewAsStudent,
      readAloudSettings
    } = dropdownActions()

    let options
    if (anySelectedProviderStudents) {
      options = [viewAsStudent, removeFromClass]
    } else if (classrooms.length > 1 && isOwnedByCurrentUser) {
      if (selectedStudentIds.length === 1) {
        options = [editAccount, resetPassword, viewAsStudent, mergeAccounts, moveClass, removeFromClass]
      } else if (selectedStudentIds.length === 2) {
        options = [viewAsStudent, mergeAccounts, moveClass, removeFromClass]
      } else {
        options = [viewAsStudent, moveClass, removeFromClass]
      }
    } else if (isOwnedByCurrentUser) {
      if (selectedStudentIds.length === 1) {
        options = [editAccount, resetPassword, viewAsStudent, mergeAccounts, removeFromClass]
      } else if (selectedStudentIds.length === 2) {
        options = [viewAsStudent, mergeAccounts, removeFromClass]
      } else {
        options = [viewAsStudent, removeFromClass]
      }
    } else {
      if (selectedStudentIds.length === 1) {
        options = [editAccount, resetPassword, viewAsStudent, removeFromClass]
      } else {
        options = [viewAsStudent, removeFromClass]
      }
    }

    if (anySelectedMergedOriginStudents) {
      options = options.filter(option => option !== mergeAccounts && option !== moveClass)
    }

    return readAloudAvailable ? [...options, readAloudSettings] : options
  }

  const renderStudentActions = () => {
    if (!classroom.visible) { return null }
    const noStudentsSelected = selectedStudentIds.length === 0

    return (
      <div className="student-actions-dropdown-wrapper">
        <DropdownInput
          className={`student-actions-dropdown bordered small ${noStudentsSelected ? 'disabled' : 'focus-on-light'}`}
          disabled={noStudentsSelected}
          handleChange={selectAction}
          label="Actions"
          options={optionsForStudentActions()}
        />
        {noStudentsSelected && <Tooltip
          tooltipText="Please select students from the list below to take action"
          tooltipTriggerText={<img alt={warningIcon.alt} src={warningIcon.src} />}
        />}
      </div>
    )
  }

  const syncedStatus = (student: any, classroomProvider: string) => {
    const { synced } = student

    if (synced === undefined || synced === null) { return '' }
    if (synced) { return 'Yes' }

    return (
      <Tooltip
        tooltipText={`This student is no longer in this class in ${classroomProvider}`}

        tooltipTriggerText={
          <div className="text-and-icon-wrapper">
            <span>No&nbsp;</span>
            <img
              alt={helpIcon.alt}
              src={helpIcon.src}
            />
          </div>
        }
      />
    )
  }

  const logInMethod = ({ email, provider }) => {
    if (provider === googleProvider) { return 'Google or email' }
    if (provider === cleverProvider) { return 'Clever' }
    if (provider === canvasProvider) { return 'Canvas' }
    if (provider === classlinkProvider) { return 'ClassLink' }
    if (provider === nycpsProvider) { return nycpsClassroomProvider }
    if (email) { return 'Username or email' }

    return 'Username'
  }

  const renderStudentDataTable = () => {
    const { classroomProvider } = classroom
    const hasClassroomProvider = classroomProvider !== undefined

    const rows = classroom.students.map(student => {
      const { name, username, email, id, provider, last_active, number_of_completed_activities, read_aloud_enabled, } = student
      const checked = !!selectedStudentIds.includes(id)
      const synced = syncedStatus(student, classroomProvider)

      return {
        synced,
        name,
        id,
        usernameOrEmail: email || username,
        checked,
        logInMethod: logInMethod({ email, provider }),
        lastActive: last_active ? moment(last_active).format('MM/DD/YY HH:mm') : '',
        activities: number_of_completed_activities,
        readAloud: onOrOff(effectiveReadAloud(read_aloud_enabled, classroom.read_aloud?.owner_default)),
        actions: classroom.visible ? actionsForIndividualStudent(student) : null
      }
    })

    return (
      <DataTable
        checkAllRows={checkAllRows}
        checkRow={checkRow}
        className='show-overflow'
        headers={classroom.visible ? activeHeaders({ hasClassroomProvider, showReadAloud: readAloudAvailable, }) : archivedHeaders(hasClassroomProvider)}
        reportingFormat={true}
        rows={rows}
        showActions={classroom.visible}
        showCheckboxes={classroom.visible}
        uncheckAllRows={uncheckAllRows}
        uncheckRow={uncheckRow}
      />
    )
  }

  const renderStudentHeader = () => {
    return (
      <div className="students-section-header with-students">
        <h3>Students</h3>
        {renderStudentHeaderButtons()}
      </div>
    )
  }

  const renderStudentHeaderButtons = () => {
    if (!classroom.visible) { return null }

    let loginPdfHref = `/teachers/classrooms/${classroom.id}/student_logins`

    let download: boolean
    if (allStudentsAreGoogle) {
      loginPdfHref = googleSetupInstructionsPdf
      download = true
    } else if (allStudentsAreClever) {
      loginPdfHref = cleverSetupInstructionsPdf
      download = true
    } else if (allStudentsAreCanvas) {
      loginPdfHref = canvasSetupInstructionsPdf
      download = true
    } else if (allStudentsAreClasslink) {
      loginPdfHref = classlinkSetupInstructionsPdf
      download = true
    }
    /* eslint-disable react/jsx-no-target-blank */
    const loginPdfLink = (
      <a
        className="quill-button secondary outlined small grey focus-on-light"
        download={download}
        href={loginPdfHref}
        rel="noopener noreferrer"
        target="_blank"
      >
        Download student log-in instructions
      </a>
    )
    /* eslint-enable react/jsx-no-target-blank */

    return (
      <div className="students-section-header-buttons">
        <div className="login-pdf-and-view-as-student top-buttons-container">
          {loginPdfLink}
          <button className="quill-button secondary outlined small grey focus-on-light" onClick={handleClickViewAsStudentButton} type="button">
            View as student
          </button>
        </div>
        {renderInviteStudents()}
      </div>
    )
  }

  const renderInviteStudents = () => {
    if (!classroom.visible) { return null }

    const { classroomProvider } = classroom

    if (classroomProvider) {
      const isDisabled = providerConfigLookup[user.provider]?.title !== classroomProvider
      const renderButton = teacherImportsStudents(classroomProvider)
      const lastUpdatedDate = moment(classroom.updated_at).format('MMM D, YYYY')

      return (
        <div className="invite-provider-classroom-students">
          {renderButton && <button
            className={`quill-button primary outlined small focus-on-light ${isDisabled ? 'disabled' : ''}`}
            disabled={isDisabled}
            onClick={importProviderClassroomStudents}
            type="button"
          >
            Import {classroomProvider} students
          </button>}
          <span>Last imported {lastUpdatedDate}</span>
        </div>
      )
    }

    return (
      <div className="invite-quill-classroom-students">
        <button className="quill-button primary outlined small focus-on-light" onClick={inviteStudents} type="button">
          Invite students
        </button>
      </div>
    )
  }

  const renderStudentSection = () => {
    if (classroom.students.length) {
      return (
        <div className="students-section">
          {renderEditStudentAccountModal()}
          {renderResetStudentPasswordModal()}
          {renderMergeStudentAccountsModal()}
          {renderMoveStudentsModal()}
          {renderRemoveStudentsModal()}
          {renderReadAloudSettingsModal()}
          {renderStudentHeader()}
          <ProviderNoteOfExplanation allStudentsAreCanvas={allStudentsAreCanvas} allStudentsAreClasslink={allStudentsAreClasslink} allStudentsAreClever={allStudentsAreClever} allStudentsAreGoogle={allStudentsAreGoogle} allStudentsAreNycps={allStudentsAreNycps} classroom={classroom} user={user} />
          {renderStudentActions()}
          {renderStudentDataTable()}
          <DuplicateAccountProTip />
        </div>
      )
    } else if (classroom.visible) {
      let copy = 'Click on the "Invite students" button to get started with your writing instruction!'
      const { classroom_provider } = classroom

      if (classroomProviders.includes(classroom_provider)) {
        copy = `Add students to your class in ${classroom_provider} and they will automatically appear here.`
      }

      return (
        <div className="students-section">
          <div className="students-section-header">
            <h3>Students</h3>
            {renderInviteStudents()}
          </div>
          <p className="no-students">{copy}</p>
        </div>
      )
    } else {
      return (
        <div className="students-section empty">
          <div className="students-section-header">
            <h3>Students</h3>
          </div>
        </div>
      )
    }
  }

  return renderStudentSection()
}

export default ClassroomStudentSection
